import { useEffect, useMemo, useState } from "react";
import {
    Alert, Autocomplete, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel,
    Grid, InputLabel, MenuItem, Select, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { ArrowBack, CloudUpload, Download } from "@mui/icons-material";
import { baseData, getData, postData } from "../../services/base.service";
import { downloadBlob, findProjectFolder, libraryPathFromFolder, SHAREPOINT_SAVE_ENABLED, uploadProjectFile } from "../../services/projectFiles.service";
import { useNotification } from "../Shared/NotificationProvider";
import { GizDay, GizDraft, ProjectBilling } from "../../types/billingTypes";

type GizTimesheetDialogProps = {
    open: boolean;
    onClose: () => void;
    onSaved?: () => void;
    // Project to generate for; when missing the dialog asks for it (used from the time report).
    projectId?: number | null;
    initialMemberId?: number | null;
    initialMonth?: string | null;
};

type MemberOption = { memberId: number; name: string; staffOid: string | null; positionId: number; bpos: number; positionTitle: string; periodName: string };
type ProjectOption = { ID: number; project_number: string | null; project_name: string | null; client: string | null };

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const previousMonth = () => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const weekday = (month: string, day: number) => {
    const [y, m] = month.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, day)).getUTCDay();
};

const ddmmyyyy = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;

function GizTimesheetDialog({ open, onClose, onSaved, projectId: fixedProjectId, initialMemberId, initialMonth }: GizTimesheetDialogProps) {
    const theme = useTheme();
    const notify = useNotification();
    const [step, setStep] = useState<'select' | 'review'>('select');
    const [projects, setProjects] = useState<ProjectOption[]>([]);
    const [projectId, setProjectId] = useState<number | null>(fixedProjectId ?? null);
    const [billing, setBilling] = useState<ProjectBilling | null>(null);
    const [memberId, setMemberId] = useState<number | null>(initialMemberId ?? null);
    const [month, setMonth] = useState(initialMonth ?? previousMonth());
    const [users, setUsers] = useState<{ oid: string; name: string }[]>([]);
    const [sourceOid, setSourceOid] = useState('');
    const [linkMember, setLinkMember] = useState(true);
    const [draft, setDraft] = useState<GizDraft | null>(null);
    const [days, setDays] = useState<GizDay[]>([]);
    const [expertName, setExpertName] = useState('');
    const [role, setRole] = useState('');
    const [billingPeriodId, setBillingPeriodId] = useState<number | ''>('');
    const [saveToSharePoint, setSaveToSharePoint] = useState(SHAREPOINT_SAVE_ENABLED);
    const [targetFolder, setTargetFolder] = useState('');
    const [bulkLocation, setBulkLocation] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setStep('select');
        setDraft(null);
        setProjectId(fixedProjectId ?? null);
        setMemberId(initialMemberId ?? null);
        setMonth(initialMonth ?? previousMonth());
        setSourceOid('');
        if (!fixedProjectId) {
            getData('projects/table').then((rows: (ProjectOption & { status: string | null; billing_type?: string })[]) =>
                setProjects(rows.filter((r) => r.status === 'Ongoing'))).catch((error) => console.error(error));
        }
        getData(`timesheets/report?from=2000-01-01&to=2100-12-31&kind=project`).then((res) => setUsers(res.users)).catch(() => setUsers([]));
    }, [open, fixedProjectId, initialMemberId, initialMonth]);

    useEffect(() => {
        if (!open || !projectId) { setBilling(null); return; }
        getData(`projects/${projectId}/billing`).then((res) => setBilling(res as ProjectBilling))
            .catch((error) => notify(`Couldn't load the positions: ${(error as Error).message}`, 'error'));
    }, [open, projectId, notify]);

    const members: MemberOption[] = useMemo(() => (billing?.periods ?? []).flatMap((period) => period.positions.flatMap((position) =>
        position.members.map((m) => ({
            memberId: m.ID, name: m.member_name, staffOid: m.staff_oid, positionId: position.ID, bpos: position.bpos,
            positionTitle: position.title, periodName: period.name,
        })))), [billing]);
    const selectedMember = members.find((m) => m.memberId === memberId) ?? null;
    const selectedPosition = billing?.periods.flatMap((p) => p.positions).find((p) => p.ID === selectedMember?.positionId) ?? null;

    const loadDraft = async () => {
        if (!projectId || !selectedMember) return;
        setBusy(true);
        try {
            if (!selectedMember.staffOid && sourceOid && linkMember) {
                await postData(`projects/${projectId}/billing/members/update`, { id: selectedMember.memberId, staff_oid: sourceOid });
            }
            const params = new URLSearchParams({ memberId: String(selectedMember.memberId), month });
            if (!selectedMember.staffOid && sourceOid) params.set('userOid', sourceOid);
            const result = await getData(`projects/${projectId}/billing/giz-timesheets/draft?${params}`) as GizDraft;
            setDraft(result);
            setDays(result.days);
            setExpertName(result.expertName);
            setRole(result.role);
            setBillingPeriodId(result.suggestedBillingPeriodId ?? '');
            if (SHAREPOINT_SAVE_ENABLED) {
                const period = result.billingPeriods.find((bp) => bp.ID === result.suggestedBillingPeriodId);
                const base = libraryPathFromFolder(billing?.project.folder) ?? (result.project.projectNumber ? await findProjectFolder(result.project.projectNumber).catch(() => null) : null);
                setTargetFolder(base ? `${base}/Invoices/GIZ/${month.slice(0, 4)}${period ? `/${ddmmyyyy(period.end_date)}` : ''}` : '');
            }
            setStep('review');
        } catch (error) {
            notify(`Couldn't prepare the timesheet: ${(error as Error).message}`, 'error');
        } finally {
            setBusy(false);
        }
    };

    const updateDay = (day: number, patch: Partial<GizDay>) => setDays((prev) => prev.map((d) => (d.day === day ? { ...d, ...patch } : d)));
    const workedDays = days.filter((d) => d.expertDays);
    const setForWorked = (patch: Partial<GizDay>) => setDays((prev) => prev.map((d) => (d.expertDays ? { ...d, ...patch } : d)));
    const allChecked = (key: 'inCountry' | 'perDiem' | 'accommodation') => workedDays.length > 0 && workedDays.every((d) => d[key]);

    const totals = days.reduce((sum, d) => ({
        domestic: sum.domestic + (d.expertDays && !d.inCountry ? d.expertDays : 0),
        abroad: sum.abroad + (d.expertDays && d.inCountry ? d.expertDays : 0),
        perDiem: sum.perDiem + (d.perDiem ? 1 : 0),
        accommodation: sum.accommodation + (d.accommodation ? 1 : 0),
    }), { domestic: 0, abroad: 0, perDiem: 0, accommodation: 0 });
    const totalDays = totals.domestic + totals.abroad;
    // Open days before this month's timesheet (a saved version of it is already counted).
    const alreadyCounted = draft?.existing ? (billing?.timesheets.find((t) => t.ID === draft.existing!.ID)?.days ?? 0) : 0;
    const openAfter = selectedPosition ? selectedPosition.remainingDays + alreadyCounted - totalDays : null;

    const save = async () => {
        if (!projectId || !draft) return;
        if (!expertName.trim() || !role.trim()) { notify('Name and role are required.', 'warning'); return; }
        setBusy(true);
        try {
            const saved = await postData(`projects/${projectId}/billing/giz-timesheets/save`, {
                memberId: draft.member.ID,
                month,
                expertName,
                role,
                billingPeriodId: billingPeriodId === '' ? null : billingPeriodId,
                days: days.map((d) => ({
                    day: d.day, expertDays: d.expertDays, inCountry: d.inCountry, perDiem: d.perDiem, accommodation: d.accommodation,
                    location: d.location || null, activity: d.activity || null,
                })),
            }) as { id: number; fileName: string };
            const file = await baseData(`projects/${projectId}/billing/giz-timesheets/${saved.id}/file`, 'GET', undefined, 'blob') as Blob;
            downloadBlob(file, saved.fileName);

            if (SHAREPOINT_SAVE_ENABLED && saveToSharePoint && targetFolder.trim()) {
                try {
                    const url = await uploadProjectFile(targetFolder.trim(), saved.fileName, file);
                    await postData(`projects/${projectId}/billing/giz-timesheets/file-url`, { id: saved.id, fileUrl: url });
                    notify(`${saved.fileName} saved and stored in SharePoint.`, 'success');
                } catch (error) {
                    notify(`Downloaded, but saving to SharePoint failed: ${(error as Error).message}`, 'warning');
                }
            } else {
                notify(`${saved.fileName} saved and downloaded.`, 'success');
            }
            onSaved?.();
            onClose();
        } catch (error) {
            notify(`Couldn't create the timesheet: ${(error as Error).message}`, 'error');
        } finally {
            setBusy(false);
        }
    };

    const headerCheckbox = (key: 'inCountry' | 'perDiem' | 'accommodation', label: string) => (
        <Tooltip title={`Tick for all days with expert-days`}>
            <FormControlLabel sx={{ margin: 0 }} labelPlacement="top"
                control={<Checkbox size="small" checked={allChecked(key)} indeterminate={!allChecked(key) && workedDays.some((d) => d[key])}
                    onChange={(e) => setForWorked({ [key]: e.target.checked } as Partial<GizDay>)} />}
                label={<Typography variant="caption" sx={{ lineHeight: 1.1 }}>{label}</Typography>} />
        </Tooltip>
    );

    return (
        <Dialog open={open} onClose={() => !busy && onClose()} fullWidth maxWidth={step === 'review' ? 'lg' : 'sm'}>
            <DialogTitle>GIZ timesheet{draft ? ` · ${draft.expertName} · ${month}` : ''}</DialogTitle>
            <DialogContent dividers>
                {step === 'select' ? (
                    <Stack gap={2.5} sx={{ paddingTop: 1 }}>
                        {!fixedProjectId && (
                            <Autocomplete options={projects} value={projects.find((p) => p.ID === projectId) ?? null}
                                onChange={(_, value) => { setProjectId(value?.ID ?? null); setMemberId(null); }}
                                getOptionLabel={(p) => `${p.project_number ?? ''} ${p.project_name ?? ''}${p.client ? ` · ${p.client}` : ''}`}
                                renderInput={(params) => <TextField {...params} label="Project" />} />
                        )}
                        {projectId && billing && members.length === 0 && (
                            <Alert severity="info">This project has no positions with members yet. Add them in the project's Billing tab first.</Alert>
                        )}
                        {members.length > 0 && (
                            <Autocomplete options={members} value={selectedMember} groupBy={(m) => `${m.periodName} · BPos ${m.bpos} ${m.positionTitle}`}
                                onChange={(_, value) => setMemberId(value?.memberId ?? null)}
                                getOptionLabel={(m) => m.name} isOptionEqualToValue={(a, b) => a.memberId === b.memberId}
                                renderInput={(params) => <TextField {...params} label="Expert and position" />} />
                        )}
                        <TextField type="month" label="Month" value={month} onChange={(e) => setMonth(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 220 }} />
                        {selectedMember && !selectedMember.staffOid && (
                            <Box>
                                <FormControl fullWidth>
                                    <InputLabel>Take the hours from the timesheet of</InputLabel>
                                    <Select label="Take the hours from the timesheet of" value={sourceOid} onChange={(e) => setSourceOid(e.target.value)}>
                                        <MenuItem value="">Nobody - fill the days by hand</MenuItem>
                                        {users.map((u) => <MenuItem key={u.oid} value={u.oid}>{u.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                {sourceOid && (
                                    <FormControlLabel control={<Checkbox checked={linkMember} onChange={(e) => setLinkMember(e.target.checked)} />}
                                        label={`Remember: ${selectedMember.name} = this timesheet user`} />
                                )}
                            </Box>
                        )}
                    </Stack>
                ) : draft && (
                    <>
                        {(!draft.project.contractNumber || !draft.project.gizProjectNumber) && (
                            <Alert severity="warning" sx={{ marginBottom: 2 }}>
                                The project has no {!draft.project.contractNumber ? 'contract number (VN)' : ''}{!draft.project.contractNumber && !draft.project.gizProjectNumber ? ' and no ' : ''}
                                {!draft.project.gizProjectNumber ? 'GIZ project number (PN)' : ''} - add it in the project's Overview tab, otherwise the header stays empty.
                            </Alert>
                        )}
                        {draft.existing && <Alert severity="info" sx={{ marginBottom: 2 }}>This month was generated before - saving replaces the earlier version.</Alert>}
                        <Grid container spacing={2} sx={{ marginBottom: 2 }}>
                            <Grid item xs={12} md={4}><TextField fullWidth size="small" label="Name of expert" value={expertName} onChange={(e) => setExpertName(e.target.value)} /></Grid>
                            <Grid item xs={12} md={4}><TextField fullWidth size="small" label="Role / position" value={role} onChange={(e) => setRole(e.target.value)} /></Grid>
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Billing period</InputLabel>
                                    <Select label="Billing period" value={billingPeriodId} onChange={(e) => setBillingPeriodId(e.target.value as number | '')}>
                                        <MenuItem value="">Not assigned yet (draft)</MenuItem>
                                        {draft.billingPeriods.map((bp) => <MenuItem key={bp.ID} value={bp.ID}>{bp.label || `Abr.per ${ddmmyyyy(bp.end_date)}`}{bp.status === 'invoiced' ? ' (invoiced)' : ''}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems={{ md: 'center' }} sx={{ marginBottom: 1.5 }}>
                            <Typography variant="body2" color="text.secondary">
                                {draft.sourceUserOid ? `${draft.hoursBooked} h booked on this project in ${month}.` : 'No timesheet user linked - fill the days by hand.'}
                                {' '}8 h = 1 day, 4 h = 0.5.
                            </Typography>
                            <Box sx={{ flexGrow: 1 }} />
                            <TextField size="small" label="Location for all worked days" value={bulkLocation} onChange={(e) => setBulkLocation(e.target.value)} sx={{ width: 240 }} />
                            <Button size="small" variant="outlined" disabled={!bulkLocation.trim()} onClick={() => setForWorked({ location: bulkLocation.trim() })}>Apply</Button>
                        </Stack>

                        <Box sx={{ maxHeight: '52vh', overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 2 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: 70 }}>Date</TableCell>
                                        <TableCell sx={{ width: 60 }} align="right">Hours</TableCell>
                                        <TableCell sx={{ width: 100 }}>Expert-days</TableCell>
                                        <TableCell align="center" sx={{ width: 80 }}>{headerCheckbox('inCountry', 'In country')}</TableCell>
                                        <TableCell align="center" sx={{ width: 80 }}>{headerCheckbox('perDiem', 'Per diem')}</TableCell>
                                        <TableCell align="center" sx={{ width: 90 }}>{headerCheckbox('accommodation', 'Accommo-dation')}</TableCell>
                                        <TableCell sx={{ width: 160 }}>Location</TableCell>
                                        <TableCell>Activity / work content / output</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {days.map((d) => {
                                        const wd = weekday(month, d.day);
                                        const weekend = wd === 0 || wd === 6;
                                        return (
                                            <TableRow key={d.day} sx={{ backgroundColor: weekend ? alpha(theme.palette.text.primary, 0.03) : undefined }}>
                                                <TableCell sx={{ color: weekend ? 'text.disabled' : 'text.primary', whiteSpace: 'nowrap' }}>{WEEKDAYS[wd]} {d.day}</TableCell>
                                                <TableCell align="right" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{d.hours ? d.hours : ''}</TableCell>
                                                <TableCell>
                                                    <Select size="small" variant="standard" value={d.expertDays ?? ''} displayEmpty sx={{ width: 70 }}
                                                        onChange={(e) => updateDay(d.day, { expertDays: e.target.value === '' ? null : Number(e.target.value) })}>
                                                        <MenuItem value="">–</MenuItem>
                                                        <MenuItem value={0.5}>0.5</MenuItem>
                                                        <MenuItem value={1}>1</MenuItem>
                                                    </Select>
                                                </TableCell>
                                                <TableCell align="center"><Checkbox size="small" checked={d.inCountry} onChange={(e) => updateDay(d.day, { inCountry: e.target.checked })} /></TableCell>
                                                <TableCell align="center"><Checkbox size="small" checked={d.perDiem} onChange={(e) => updateDay(d.day, { perDiem: e.target.checked })} /></TableCell>
                                                <TableCell align="center"><Checkbox size="small" checked={d.accommodation} onChange={(e) => updateDay(d.day, { accommodation: e.target.checked })} /></TableCell>
                                                <TableCell><TextField variant="standard" size="small" fullWidth value={d.location} inputProps={{ maxLength: 100 }}
                                                    onChange={(e) => updateDay(d.day, { location: e.target.value })} /></TableCell>
                                                <TableCell><TextField variant="standard" size="small" fullWidth value={d.activity} inputProps={{ maxLength: 1000 }}
                                                    onChange={(e) => updateDay(d.day, { activity: e.target.value })} /></TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Box>

                        <Stack direction="row" gap={3} flexWrap="wrap" sx={{ marginTop: 1.5 }}>
                            <Typography variant="body2"><b>{totalDays}</b> expert-days ({totals.domestic} domestic, {totals.abroad} in country)</Typography>
                            <Typography variant="body2">{totals.perDiem} per diem · {totals.accommodation} nights</Typography>
                            {openAfter !== null && (
                                <Typography variant="body2" color={openAfter < 0 ? 'error' : 'text.secondary'}>
                                    BPos {draft.position.bpos}: <b>{openAfter}</b> days open after this timesheet{openAfter < 0 ? ' - over budget' : ''}
                                </Typography>
                            )}
                        </Stack>

                        {SHAREPOINT_SAVE_ENABLED && (
                        <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems={{ md: 'center' }} sx={{ marginTop: 2 }}>
                            <FormControlLabel control={<Switch checked={saveToSharePoint} onChange={(e) => setSaveToSharePoint(e.target.checked)} />} label="Also save to SharePoint" />
                            <TextField size="small" fullWidth disabled={!saveToSharePoint} label="Folder in INT-EE - Documents" value={targetFolder}
                                placeholder="Projects/6066 GIZ NGA NESP_DGM/Invoices/GIZ/2026" onChange={(e) => setTargetFolder(e.target.value)} />
                        </Stack>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                {step === 'review' && <Button startIcon={<ArrowBack />} onClick={() => setStep('select')} disabled={busy} sx={{ marginRight: 'auto' }}>Back</Button>}
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                {step === 'select' ? (
                    <Button variant="contained" disableElevation disabled={!selectedMember || !month || busy} onClick={loadDraft}>
                        {busy ? <CircularProgress size={18} /> : 'Continue'}
                    </Button>
                ) : (
                    <Button variant="contained" disableElevation onClick={save} disabled={busy}
                        startIcon={busy ? <CircularProgress size={16} /> : SHAREPOINT_SAVE_ENABLED && saveToSharePoint ? <CloudUpload /> : <Download />}>
                        Save and download
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

export default GizTimesheetDialog;
